/**
 * @file Construction du timestamp ISO d'une réservation depuis l'état du formulaire.
 * @module packages.common.components.table-reservation.utils.buildReservationDateTime
 *
 * Source unique de la logique de formatage date + créneau, partagée entre la
 * soumission finale (useReservationHandlers) et le sync du brouillon de
 * parcours (useReservationDraftSync). Les créneaux overnight (ex: 01:00)
 * tombent sur J+1 : seul le timestamp pré-calculé du créneau sélectionné est
 * fiable pour eux, le fallback texte ne corrige pas le jour.
 */

/**
 * Construit la chaîne dateTime envoyée au backend pour une réservation.
 *
 * @param {Object} formData - État du formulaire de réservation
 * @param {Date|string} formData.date - Date sélectionnée (Date ou 'YYYY-MM-DD')
 * @param {string} formData.timeSlot - Créneau sélectionné ('HH:MM')
 * @param {Object} [formData.selectedTimeSlotData] - Créneau enrichi (timestamp pré-calculé)
 * @return {string} Timestamp ISO ou chaîne locale 'YYYY-MM-DDTHH:MM:00'
 * @throws {Error} Si la date ou le créneau est absent, ou la date d'un type non supporté
 */
export function buildReservationDateTime(formData) {
  const selectedDate = formData.date;

  if (!selectedDate || !formData.timeSlot) {
    throw new Error('Date et créneau horaire requis');
  }

  // Les créneaux overnight (ex: 01:00) tombent sur J+1 : le timestamp pré-calculé est fiable
  if (formData.selectedTimeSlotData && formData.selectedTimeSlotData.timestamp) {
    return new Date(formData.selectedTimeSlotData.timestamp).toISOString();
  }

  // Fallback sans timestamp : risque d'erreur pour les créneaux overnight
  let dateString;
  if (selectedDate instanceof Date) {
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    dateString = `${year}-${month}-${day}`;
  } else if (typeof selectedDate === 'string') {
    dateString = selectedDate;
  } else {
    throw new Error('Type de date non supporté');
  }

  return `${dateString}T${formData.timeSlot}:00`;
}
